import cloneDeep from 'lodash-es/cloneDeep';
import eq from 'lodash-es/eq';
import isPlainObject from 'lodash-es/isPlainObject';
import { PartialDeep } from 'type-fest';

// Replaces `cloneDeep` + lodash `merge` on the cache write path, which destroyed
// referential identity for every object in the cache.

const isContainer = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

type AnyRecord = Record<string, unknown>;

const shallowCopy = (node: AnyRecord): AnyRecord =>
  Array.isArray(node) ? ([...node] as unknown as AnyRecord) : { ...node };

// Copies a node only the first time one of its keys changes.
const mergeNode = (target: AnyRecord, source: AnyRecord): AnyRecord => {
  let result = target;
  const copyOnce = () => {
    if (result === target) {
      result = shallowCopy(target);
    }
  };

  // Source order. For an array that is the indices, which merges by index.
  for (const key of Object.keys(source)) {
    // Prototype-pollution guard. lodash drops this key too.
    if (key === '__proto__') {
      continue;
    }

    const srcVal = source[key];
    const tgtVal = target[key];

    if (srcVal === undefined) {
      // `undefined` never overwrites, but it does create a key that is missing.
      if (!(key in target)) {
        copyOnce();
        result[key] = undefined;
      }
      continue;
    }

    if (Array.isArray(srcVal) || isPlainObject(srcVal)) {
      // A plain-object source merges into any container, arrays included; an array
      // source needs an array target. Anything else adopts a deep copy.
      const mergeable = Array.isArray(srcVal) ? Array.isArray(tgtVal) : isContainer(tgtVal);
      const child = mergeable
        ? mergeNode(tgtVal as AnyRecord, srcVal as AnyRecord)
        : cloneDeep(srcVal);

      if (child !== (tgtVal as unknown)) {
        copyOnce();
        result[key] = child;
      }
      continue;
    }

    if (!eq(tgtVal, srcVal)) {
      copyOnce();
      result[key] = isContainer(srcVal) ? cloneDeep(srcVal) : srcVal;
    }
  }

  return result;
};

/**
 * Copy-on-write equivalent of lodash `merge(cloneDeep(target), source)`, for
 * JSON-ish data only. Returns `target` itself on a value no-op, so callers can
 * use `===` to detect "nothing happened". `MERGE_CASES` in this module's test
 * pins the semantics against real lodash; outside the JSON-ish contract the two
 * diverge in several ways, none reachable for these payloads.
 *
 * @param target - the value being updated; left untouched
 * @param source - a deep partial to apply
 * @returns the merged value, or `target` itself if nothing changed
 */
export const mergeCopyOnWrite = <T>(target: T, source: NoInfer<PartialDeep<T>>): T => {
  if (!isContainer(target) || !isContainer(source)) {
    return target;
  }
  return mergeNode(target as AnyRecord, source as AnyRecord) as T;
};

/**
 * Applies one partial to many bases, memoized per base, so a socket event that
 * fans out to dozens of query keys holding the same object merges once.
 *
 * Memoized on base identity: a base mutated in place keeps returning the
 * pre-mutation result. Safe here only because nothing mutates a cached
 * conversation.
 *
 * @param partial - the deep partial to apply to every base
 * @returns a merger function; call it once per cached base object
 */
export const createCowMerger = <T extends object>(partial: PartialDeep<T>) => {
  const memo = new WeakMap<object, T>();

  return (base: T): T => {
    if (memo.has(base)) {
      return memo.get(base);
    }
    const next = mergeCopyOnWrite(base, partial);
    memo.set(base, next);
    return next;
  };
};
